/**
 * generateStructuredAssets
 * Detects mission category from title/text and generates structured named
 * business asset files from real AI output (actionCards, completedSteps).
 *
 * Returns an array of asset objects:
 * { filename, format, mimeType, description, content }
 */

// ─── Mission Category Detection ───────────────────────────────────────────────

const SALES_CREW_KEYWORDS = [
  // Exact template names
  'lead reactivation', 'sales follow-up', 'follow-up system', 'outbound engine',
  'buyer conversion', 'pipeline movement', 'lead qualification', 'sales crew',
  'revenue recovery', 'buyer conversion system', 'lead automation',
  // Casual user phrasings (Task 2 + Task 5)
  'help me sell', 'sell more', 'follow up', 'follow-up', 'old leads', 'cold leads',
  'stalled leads', 'no reply', 'no response', 'sales script', 'buyer script',
  'close more', 'convert leads', 'qualify leads', 'lead sorting', 'lead scoring',
  'sales pipeline', 'revive leads', 'reactivation', 'outreach', 'objections',
  'booking', 'viewing', 'reservation', 'payment ready', 'interested buyer',
  // Casual mission phrasings
  'write follow up', 'write follow-up', 'reactivate old', 'reactivate leads',
  'reactivate customers', 'make sales scripts', 'organize my prospects',
  'sort my leads', 'build a buyer', 'create a sales', 'recover no-response',
  'sales system', 'lead system', 'outreach system', 'sales playbook',
  'objection', 'pipeline', 'lead nurture', 'nurture sequence', 'drip sequence',
  'follow-up sequence', 'delivery handoff', 'delivery crew', 'handoff',
  'buyer qualification', 'cold prospects', 'stalled conversations',
];

// Delivery-specific terms used to conditionally generate delivery_handoff_checklist.md
const DELIVERY_KEYWORDS = [
  'delivery', 'handoff', 'hand-off', 'onboarding', 'fulfillment', 'client',
  'post-sale', 'post sale', 'deliver', 'completed order',
];

export function detectMissionCategory(missionTitle = '', missionText = '') {
  const combined = `${missionTitle} ${missionText}`?.toLowerCase();
  const isSalesCrew = SALES_CREW_KEYWORDS?.some(kw => combined?.includes(kw));
  return isSalesCrew ? 'sales_crew' : 'generic';
}

function hasDeliveryContext(missionTitle = '', missionText = '', allText = '') {
  const combined = `${missionTitle} ${missionText} ${allText}`?.toLowerCase();
  return DELIVERY_KEYWORDS?.some(kw => combined?.includes(kw));
}

// ─── Content Extraction Helpers ───────────────────────────────────────────────

function extractAllText(actionCards = [], completedSteps = {}) {
  const parts = [];

  if (actionCards?.length > 0) {
    actionCards?.forEach(card => {
      const text = card?.full_output || card?.output || card?.content || card?.description || '';
      if (text) parts?.push(`## ${card?.title || card?.action || 'Output'}\n\n${text}`);
    });
  }

  const steps = Object.values(completedSteps || {});
  if (steps?.length > 0) {
    steps?.forEach(step => {
      const text = step?.full_output || step?.output || step?.content || '';
      if (text) parts?.push(`## ${step?.action || step?.id || 'Step'}\n\n${text}`);
    });
  }

  return parts?.join('\n\n---\n\n');
}

/**
 * findCardByKeywords — Task 1
 * Searches card.title, card.action, card.filename, card.full_output,
 * card.output, and card.content for keyword matches.
 */
function findCardByKeywords(actionCards = [], completedSteps = {}, keywords = []) {
  const allCards = [
    ...actionCards,
    ...Object.values(completedSteps || {}),
  ];

  for (const kw of keywords) {
    const kwLower = kw?.toLowerCase();
    const found = allCards?.find(c => {
      // Search all text fields — title, action, filename, full_output, output, content
      const title = (c?.title || '')?.toLowerCase();
      const action = (c?.action || '')?.toLowerCase();
      const filename = (c?.filename || '')?.toLowerCase();
      const fullOutput = (c?.full_output || '')?.toLowerCase();
      const output = (c?.output || '')?.toLowerCase();
      const content = (c?.content || '')?.toLowerCase();

      return (title?.includes(kwLower) ||
      action?.includes(kwLower) ||
      filename?.includes(kwLower) ||
      fullOutput?.includes(kwLower) ||
      output?.includes(kwLower) || content?.includes(kwLower));
    });
    if (found) return found?.full_output || found?.output || found?.content || found?.description || '';
  }
  return '';
}

/**
 * extractMissionIntelligence — Task 3
 * Pulls a clean, short excerpt from allText for the Mission-Specific Intelligence section.
 * Avoids dumping huge raw text. Returns null if allText is empty.
 */
function extractMissionIntelligence(allText = '', maxLength = 600) {
  if (!allText || allText?.trim()?.length < 30) return null;
  // Try to find the first substantive paragraph (not a heading)
  const lines = allText?.split('\n')?.filter(l => l?.trim()?.length > 40 && !l?.trim()?.startsWith('#'));
  if (lines?.length === 0) return allText?.slice(0, maxLength);
  const excerpt = lines?.slice(0, 4)?.join('\n');
  return excerpt?.length > maxLength ? excerpt?.slice(0, maxLength) + '...' : excerpt;
}

/**
 * sanitizeCsvCell — Task 4 (CSV fix)
 * Replaces newlines and carriage returns inside CSV cell values with a space.
 */
function sanitizeCsvCell(value = '') {
  return String(value)?.replace(/\r\n/g, ' ')?.replace(/\r/g, ' ')?.replace(/\n/g, ' ')?.replace(/"/g, '""');
}

function extractLine(text, index) {
  const lines = text?.split('\n')?.filter(l => l?.trim()?.length > 20);
  const line = lines?.[index] || lines?.[0] || text?.slice(0, 200);
  // Sanitize newlines from extracted lines used in CSV
  return line ? line?.replace(/\r?\n/g, ' ')?.trim() : '';
}

// ─── Asset Generators ─────────────────────────────────────────────────────────

function generateSalesPlaybook(missionTitle, allText, actionCards, completedSteps) {
  const icp = findCardByKeywords(actionCards, completedSteps, ['icp', 'ideal customer', 'audience', 'target', 'profile', 'buyer persona', 'customer profile']);
  const offer = findCardByKeywords(actionCards, completedSteps, ['offer', 'positioning', 'value prop', 'pitch', 'product', 'service', 'solution']);
  const qualify = findCardByKeywords(actionCards, completedSteps, ['qualify', 'qualification', 'criteria', 'scoring', 'lead score', 'intent']);
  const intent = findCardByKeywords(actionCards, completedSteps, ['intent', 'signal', 'buying signal', 'interest', 'engagement', 'hot lead']);
  const objection = findCardByKeywords(actionCards, completedSteps, ['objection', 'concern', 'pushback', 'resistance', 'hesitation', 'objections']);
  const followup = findCardByKeywords(actionCards, completedSteps, ['follow', 'sequence', 'cadence', 'outreach', 'follow-up', 'follow up']);
  const takeover = findCardByKeywords(actionCards, completedSteps, ['takeover', 'escalate', 'manual', 'human', 'handoff', 'hand off']);
  const intelligence = extractMissionIntelligence(allText);

  return `# Sales Playbook
## Generated by RecallCore — ${missionTitle || 'Sales Crew Mission'}

---

## Executive Summary

This playbook was generated from your Sales Crew mission execution. It consolidates the key components of your Buyer Conversion System into a single deployable reference document.

${intelligence ? `### Mission-Specific Intelligence\n\n${intelligence}\n` : ''}

---

## Ideal Customer Profile (ICP)

${icp ? icp?.slice(0, 800) : `**Primary Buyer:**
- Decision-maker with budget authority
- Actively looking or recently expressed interest
- Has a clear problem your offer solves
- Timeline: 30–90 days to purchase

**Disqualifiers:**
- No budget or timeline
- Not the decision-maker
- Already locked into a competitor contract`}

---

## Offer Positioning

${offer ? offer?.slice(0, 800) : `**Core Offer:**
State your primary offer clearly in one sentence.

**Key Differentiators:**
1. Speed of delivery vs. competitors
2. Specific outcome guarantee
3. Unique mechanism or process

**Positioning Statement:**
"For [ICP], we deliver [outcome] without [pain point], unlike [alternative]."

**Price Anchoring:**
Lead with value, not price. Present ROI before cost.`}

---

## Lead Qualification Rules

${qualify ? qualify?.slice(0, 800) : `**Qualify a lead when they have:**
- [ ] Budget confirmed or estimated
- [ ] Clear use case or problem stated
- [ ] Decision-maker identified
- [ ] Timeline within 90 days
- [ ] Responded to at least 2 touchpoints

**Score 1–10:**
- 8–10: Hot — immediate follow-up, personal outreach
- 5–7: Warm — nurture sequence, check-in every 3–5 days
- 1–4: Cold — reactivation sequence, monthly touch`}

---

## Buyer Intent Signals

${intent ? intent?.slice(0, 800) : `**High Intent:**
- Asks about price, payment, or delivery
- Requests a demo, call, or meeting
- Shares specific use case or timeline
- Responds quickly to messages
- Visits pricing page multiple times

**Medium Intent:**
- Opens emails but doesn't reply
- Asks general questions
- Engages with content

**Low Intent / Stale:**
- No response in 7+ days
- Unsubscribed from sequence
- Said "not right now" without timeline`}

---

## Objection Handling

${objection ? objection?.slice(0, 800) : `**Price Objection:**
"I understand — let me show you what this replaces and the ROI timeline."

**Need to Think:**
"Totally fair. What's the one thing you'd need to feel confident moving forward?"

**Trust Concern:**
"Here's what [similar client] said after 30 days. Want me to connect you with them?"

**Timing:**
"What would need to change for this to be the right time? Let's plan for that."`}

---

## Follow-Up Engine

${followup ? followup?.slice(0, 800) : `**Day 0:** Initial outreach — introduce offer, ask qualifying question
**Day 1:** Follow-up if no response — add value, share proof
**Day 3:** Check-in — different angle, address likely objection
**Day 7:** Final message — soft close, create urgency
**Day 14:** Reactivation — "Still open to connecting?"
**Day 30:** Long-term nurture — share win, case study, or update`}

---

## Manual Takeover Rules

${takeover ? takeover?.slice(0, 600) : `Escalate to human immediately when:
- Lead asks for a call or meeting
- Lead mentions a specific dollar amount
- Lead expresses frustration or urgency
- Lead has responded 3+ times in the same thread
- Lead is a referral from an existing client`}

---

*Generated by RecallCore Sales Crew · Deploy immediately*
`;
}

function generateFollowUpSequenceCSV(missionTitle, allText, actionCards, completedSteps) {
  const sequenceText = findCardByKeywords(actionCards, completedSteps, [
    'sequence', 'follow', 'cadence', 'outreach', 'email', 'message',
    'follow-up', 'follow up', 'drip', 'nurture', 'script',
  ]);

  // Build CSV rows — sanitize all cell values to prevent row breaks
  const rows = [
    ['step_number', 'timing', 'channel', 'message_type', 'message_copy', 'trigger_condition', 'next_action'],
    [
      '1',
      'Day 0 — Immediately',
      'SMS or Email',
      'Introduction',
      sequenceText ? extractLine(sequenceText, 0) : "Hi [Name], I saw you were interested in [offer]. Quick question — what's your main goal right now?",
      'New lead enters pipeline',
      'Wait for reply — if no reply in 24h go to Step 2',
    ],
    [
      '2',
      'Day 1 — 24h after Step 1',
      'Email',
      'Value Add',
      sequenceText ? extractLine(sequenceText, 1) : "Hey [Name], sharing a quick win from a client in your situation: [result]. Worth a 10-min call?",
      'No reply to Step 1',
      'Wait 48h — if no reply go to Step 3',
    ],
    [
      '3',
      'Day 3 — 72h after Step 1',
      'SMS',
      'Objection Preempt',
      sequenceText ? extractLine(sequenceText, 2) : "[Name], most people I talk to worry about [objection]. Here's how we handle that: [solution]. Still interested?",
      'No reply to Step 2',
      'Wait 4 days — if no reply go to Step 4',
    ],
    [
      '4',
      'Day 7 — 1 week after Step 1',
      'Email',
      'Soft Close',
      sequenceText ? extractLine(sequenceText, 3) : "Last message from me for now, [Name]. If timing isn't right, no worries. When you're ready: [CTA link]",
      'No reply to Step 3',
      'Move to cold nurture — monthly touch',
    ],
    [
      '5',
      'Day 14 — Reactivation',
      'SMS',
      'Reactivation',
      sequenceText ? extractLine(sequenceText, 4) : "Hey [Name], circling back. We just [recent win/update]. Thought of you — still open to a quick chat?",
      'Lead went cold after Day 7',
      'If reply: book call. If no reply: 30-day nurture',
    ],
    [
      '6',
      'Day 30 — Long Nurture',
      'Email',
      'Case Study Share',
      sequenceText ? extractLine(sequenceText, 5) : "[Name], thought you'd find this useful — [client] just achieved [result] using our system. Full story here: [link]",
      'No reply to Day 14',
      'Monthly nurture until reply or unsubscribe',
    ],
    [
      '7',
      'Trigger-based',
      'SMS',
      'High-Intent Reply',
      "Thanks for getting back to me! Let's get you on the calendar — here's my link: [booking link]",
      'Lead replies to any step',
      'Book call immediately — do not delay',
    ],
  ];

  // Task 4: sanitize every cell value — replace \n and \r with spaces
  // === CSV-BOM-AT-SOURCE v1 ===
  // UTF-8 BOM prefix so Excel detects encoding and renders em-dashes/accents correctly
  const csvBody = rows?.map(r => r?.map(cell => `"${sanitizeCsvCell(cell)}"`)?.join(','))?.join('\n');
  return '\uFEFF' + csvBody;
}

function generateObjectionScripts(missionTitle, allText, actionCards, completedSteps) {
  const objectionText = findCardByKeywords(actionCards, completedSteps, [
    'objection', 'concern', 'pushback', 'resistance', 'hesitation',
    'price objection', 'trust', 'not sure', 'thinking about it',
  ]);
  const reactivationText = findCardByKeywords(actionCards, completedSteps, [
    'reactivation', 'revival', 'cold', 'stalled', 'silent', 'no reply', 'no response',
  ]);
  const intelligence = extractMissionIntelligence(allText, 500);

  const hasMissionObjections = objectionText && objectionText?.trim()?.length > 50;

  return `# Objection Scripts
## Generated by RecallCore — ${missionTitle || 'Sales Crew Mission'}

---

${intelligence ? `## Mission-Specific Intelligence\n\n${intelligence}\n\n---\n` : ''}

${hasMissionObjections ? `## Mission-Specific Objections & Responses\n\n${objectionText?.slice(0, 1000)}\n\n---\n` : '## Default Operating Scripts\n\n*The following are default operating scripts. Customize with your specific offer details.*\n\n---\n'}

## Price Objection

**Lead says:** "It's too expensive" / "I can't afford it right now"

**Response:**
"I hear you — and I want to make sure you're making the right decision, not just the cheapest one. Let me ask: what's it costing you right now to NOT have this solved? Most of our clients find the ROI shows up in the first 30 days. If budget is the real issue, we can look at a phased approach. What would make this feel like a no-brainer?"

---

## Need-to-Think Objection

**Lead says:** "Let me think about it" / "I need to discuss with my partner"

**Response:**
"Totally understand — this is a real decision. What's the one thing you'd need to feel fully confident?

I want to make sure you have everything you need. Is it more proof? A clearer timeline? Or is there a specific concern I haven't addressed yet?"

---

## Trust Concern

**Lead says:** "How do I know this works?" / "I've tried things like this before"

**Response:**
"That's a fair concern — and honestly, I'd rather you be skeptical than just take my word for it.

Here's what I can do: let me show you exactly what [similar client] achieved in their first 60 days. Real numbers, real results.

Would that help you feel more confident about moving forward?"

---

## Delivery Concern

**Lead says:** "How long does this take?" / "When will I see results?"

**Response:**
"Great question — and I want to be honest with you, not just tell you what you want to hear.

Most clients see [specific result] within [timeframe]. The first [X days] are setup and onboarding. After that, [outcome] typically kicks in.

I'll give you a clear timeline before we start so there are no surprises."

---

## Financing / Payment Concern

**Lead says:** "Can I pay in installments?" / "Do you have a payment plan?"

**Response:**
"Yes — we have options. Let me walk you through what's available so we can find what works for your situation.

The most popular option is [payment plan]. It keeps the investment manageable while you start seeing results.

Want me to send you the breakdown?"

---

## No-Response Revival

**Lead went silent — use this:**

"Hey [Name], I know life gets busy. No pressure at all.

I just wanted to check in one last time — if the timing isn't right, totally understand. But if you're still thinking about [goal], I'd love to help.

One question: what would need to change for this to make sense for you right now?"

---

## High-Intent Buyer Response

**Lead says:** "I'm ready" / "How do we start?" / "What's the next step?"

**Response:**
"Amazing — let's get you locked in. Here's what happens next:
1. [Step 1 — e.g., sign agreement]
2. [Step 2 — e.g., onboarding call]
3. [Step 3 — e.g., first deliverable]

I'll send you the link right now. Any questions before we move forward?"

---

${reactivationText ? `## Mission-Specific Reactivation Scripts\n\n${reactivationText?.slice(0, 600)}\n\n---\n` : ''}

*Generated by RecallCore Sales Crew · Customize with your offer details before deploying*
`;
}

function generatePipelineConfig(missionTitle, allText, actionCards, completedSteps) {
  const pipelineText = findCardByKeywords(actionCards, completedSteps, [
    'pipeline', 'stage', 'status', 'lifecycle', 'funnel', 'stages',
    'pipeline movement', 'lead status', 'opportunity',
  ]);
  const intelligence = extractMissionIntelligence(allText, 800);

  const config = {
    mission_title: missionTitle || 'Sales Crew Mission',
    generated_by: 'RecallCore',
    mission_specific_context: pipelineText
      ? pipelineText?.slice(0, 500)?.replace(/\n/g, ' ')?.trim()
      : (intelligence ? intelligence?.replace(/\n/g, ' ')?.trim()?.slice(0, 500) : null),
    stages: [
      { id: 'intake', label: 'New Lead', description: 'Lead just entered pipeline', order: 1 },
      { id: 'contacted', label: 'Contacted', description: 'First outreach sent', order: 2 },
      { id: 'engaged', label: 'Engaged', description: 'Lead has responded', order: 3 },
      { id: 'qualified', label: 'Qualified', description: 'Budget, timeline, and need confirmed', order: 4 },
      { id: 'proposal', label: 'Proposal Sent', description: 'Offer or quote delivered', order: 5 },
      { id: 'negotiating', label: 'Negotiating', description: 'In active discussion', order: 6 },
      { id: 'closed_won', label: 'Closed — Won', description: 'Deal confirmed, payment received', order: 7 },
      { id: 'closed_lost', label: 'Closed — Lost', description: 'Did not convert', order: 8 },
      { id: 'reactivation', label: 'Reactivation Queue', description: 'Cold lead flagged for revival', order: 9 },
    ],
    lead_status_rules: {
      new: 'No contact made yet — assign to Follow-Up Engine immediately',
      contacted: 'At least 1 outreach sent — awaiting response',
      engaged: 'Lead has replied at least once — qualify within 24h',
      qualified: 'All 5 qualification criteria met — move to proposal',
      stalled: 'No activity in 5+ days — trigger Revenue Recovery sequence',
      lost: 'Explicitly declined or unresponsive after full sequence',
    },
    hot_warm_cold_stale_thresholds: {
      hot: { intent_score_min: 75, description: 'Responded quickly, asked about price or delivery, high engagement', action: 'Personal outreach within 1 hour' },
      warm: { intent_score_min: 50, intent_score_max: 74, description: 'Engaged but not urgent, needs nurturing', action: 'Follow-up within 24h, add value' },
      cold: { intent_score_min: 1, intent_score_max: 49, description: 'Low engagement, may need education', action: 'Automated nurture sequence, monthly touch' },
      stale: { days_since_contact_min: 7, description: 'No activity in 7+ days regardless of score', action: 'Reactivation sequence — 4-message revival' },
    },
    manual_takeover_triggers: [
      'Lead asks for a call or meeting',
      'Lead mentions a specific dollar amount or budget',
      'Lead expresses frustration or urgency',
      'Lead has replied 3+ times in the same thread',
      'Lead is a referral from an existing client',
      'Lead asks about contract terms or legal questions',
      'Lead requests a custom quote or proposal',
    ],
    conversion_events: [
      { event: 'payment_received', action: 'Move to closed_won, trigger Delivery Handoff' },
      { event: 'contract_signed', action: 'Move to negotiating → closed_won' },
      { event: 'deposit_paid', action: 'Flag as confirmed, begin onboarding' },
      { event: 'booking_confirmed', action: 'Move to qualified → proposal' },
      { event: 'declined', action: 'Move to closed_lost, log reason' },
    ],
    recommended_next_actions: {
      intake: 'Send introduction message within 5 minutes of lead entry',
      contacted: 'Follow up in 24h if no response',
      engaged: 'Qualify with 3 key questions: budget, timeline, decision-maker',
      qualified: 'Send proposal or offer within 24h',
      proposal: 'Follow up in 48h, address top objection',
      negotiating: 'Resolve final objection, create urgency with deadline',
      stalled: 'Trigger 4-step Revenue Recovery sequence',
      lost: 'Log reason, add to 90-day nurture list',
    },
  };

  return JSON.stringify(config, null, 2);
}

function generateBuyerQualificationChecklist(missionTitle, allText, actionCards, completedSteps) {
  const qualText = findCardByKeywords(actionCards, completedSteps, [
    'qualify', 'qualification', 'buyer', 'criteria', 'checklist',
    'icp', 'ideal customer', 'profile', 'use case', 'use-case',
  ]);
  const intelligence = extractMissionIntelligence(allText, 500);

  // Build mission-specific qualification questions if AI output has buyer/ICP info
  const hasMissionQual = qualText && qualText?.trim()?.length > 50;

  return `# Buyer Qualification Checklist
## Generated by RecallCore — ${missionTitle || 'Sales Crew Mission'}

Use this checklist before moving a lead to the proposal stage.
A lead must pass at least 4 of 7 criteria to be considered qualified.

---

${intelligence ? `## Mission-Specific Intelligence\n\n${intelligence}\n\n---\n` : ''}

${hasMissionQual ? `## Mission-Specific Qualification Criteria\n\n${qualText?.slice(0, 700)}\n\n---\n` : ''}

## Qualification Criteria

### 1. Budget Readiness
- [ ] Lead has confirmed they have budget available
- [ ] Budget range aligns with your offer price
- [ ] Payment method is available (cash, financing, installments)
- [ ] No financial blockers mentioned

**Notes:** _______________________________________________

---

### 2. Use Case Clarity
- [ ] Lead has described a specific problem or goal
- [ ] Your offer directly solves their stated problem
- [ ] Lead understands what they're buying
- [ ] No major misalignment in expectations

**Notes:** _______________________________________________

---

### 3. Timeline
- [ ] Lead has a specific timeline or deadline
- [ ] Timeline is within 90 days
- [ ] No major delays expected (waiting on funding, partner approval, etc.)
- [ ] Urgency is real, not manufactured

**Notes:** _______________________________________________

---

### 4. Decision Maker
- [ ] You are speaking directly with the decision-maker
- [ ] If not, decision-maker has been identified and is accessible
- [ ] No committee approval required that would delay close
- [ ] Lead has authority to say yes without additional sign-off

**Notes:** _______________________________________________

---

### 5. Payment Readiness
- [ ] Lead has asked about payment options
- [ ] Lead has not raised major price objections
- [ ] Deposit or full payment can be processed immediately upon agreement
- [ ] Payment method confirmed (card, bank transfer, etc.)

**Notes:** _______________________________________________

---

### 6. Viewing / Demo Readiness
- [ ] Lead has agreed to a call, demo, or viewing
- [ ] Appointment is scheduled or lead is responsive to scheduling
- [ ] Lead showed up to previous scheduled interaction
- [ ] No ghosting pattern observed

**Notes:** _______________________________________________

---

### 7. Reservation / Commitment Readiness
- [ ] Lead has expressed willingness to reserve or commit
- [ ] Lead has asked "what's the next step?"
- [ ] Lead has shared personal details (name, contact, address)
- [ ] Lead has not said "I need to think about it" more than once

**Notes:** _______________________________________________

---

## Qualification Score

| Criteria | Pass | Fail | Notes |
|---|---|---|---|
| Budget Readiness | | | |
| Use Case Clarity | | | |
| Timeline | | | |
| Decision Maker | | | |
| Payment Readiness | | | |
| Viewing Readiness | | | |
| Reservation Readiness | | | |

**Total Score: ___ / 7**

- **5–7:** Fully qualified — move to proposal immediately
- **3–4:** Partially qualified — address gaps before proposal
- **0–2:** Not yet qualified — continue nurturing

---

*Generated by RecallCore Sales Crew · Fill in before each proposal*
`;
}

function generateLeadReactivationSequence(missionTitle, allText, actionCards, completedSteps) {
  const reactivationText = findCardByKeywords(actionCards, completedSteps, [
    'reactivation', 'revival', 'cold', 'stalled', 'silent', 'old lead',
    'inactive', 'no reply', 'no response', 'outreach', 'revive',
  ]);
  const scriptText = findCardByKeywords(actionCards, completedSteps, [
    'script', 'message', 'copy', 'outreach', 'sequence', 'follow-up', 'follow up',
  ]);
  const intelligence = extractMissionIntelligence(allText, 600);

  // Use mission output lines for day messages when available
  const day0Msg = reactivationText ? extractLine(reactivationText, 0) : null;
  const day1Msg = reactivationText ? extractLine(reactivationText, 1) : (scriptText ? extractLine(scriptText, 0) : null);
  const day3Msg = reactivationText ? extractLine(reactivationText, 2) : (scriptText ? extractLine(scriptText, 1) : null);
  const day7Msg = reactivationText ? extractLine(reactivationText, 3) : (scriptText ? extractLine(scriptText, 2) : null);

  return `# Lead Reactivation Sequence
## Generated by RecallCore — ${missionTitle || 'Lead Reactivation Mission'}

Use this sequence for leads that have gone cold, stalled, or stopped responding.
Deploy in order. Stop when the lead replies — switch to live conversation immediately.

---

${intelligence ? `## Mission-Specific Intelligence\n\n${intelligence}\n\n---\n` : ''}

${reactivationText ? `## Mission-Specific Reactivation Output\n\n${reactivationText?.slice(0, 800)}\n\n---\n` : ''}
${scriptText && !reactivationText ? `## Mission-Specific Script Output\n\n${scriptText?.slice(0, 800)}\n\n---\n` : ''}

## Day 0 — Re-Entry Message

**Channel:** SMS (preferred) or Email
**Timing:** Send immediately when lead is flagged as stalled/cold

**Message:**
${day0Msg ? `"${day0Msg}"` : `"Hey [Name], [Your Name] here. I know it's been a while — hope things are going well.

I wanted to reach out because [recent update / new offer / relevant news].

Still open to a quick conversation? No pressure at all."`}

**Goal:** Re-open the door without pressure. Keep it short.

---

## Day 1 — Value Follow-Up

**Channel:** Email
**Timing:** 24 hours after Day 0 if no reply

**Message:**
${day1Msg ? `"${day1Msg}"` : `"Hi [Name],

Just wanted to share something that made me think of you — [client result / case study / relevant win].

[Client Name] was in a similar situation to yours and achieved [specific result] in [timeframe].

Thought it might be useful. Happy to share the full story if you're curious."`}

**Goal:** Provide value without asking for anything. Build credibility.

---

## Day 3 — Objection Preempt

**Channel:** SMS
**Timing:** 3 days after Day 0 if no reply

**Message:**
${day3Msg ? `"${day3Msg}"` : `"Hey [Name], quick one —

A lot of people I talk to hesitate because of [common objection — e.g., timing, price, uncertainty].

I get it. Here's how we handle that: [brief solution].

Still worth a 10-minute call?"`}

**Goal:** Address the most likely reason they went silent.

---

## Day 7 — Final Message

**Channel:** Email
**Timing:** 7 days after Day 0 if no reply

**Subject:** Closing your file

**Message:**
${day7Msg ? `"${day7Msg}"` : `"Hi [Name],

I don't want to keep filling your inbox if the timing isn't right — so this will be my last message for now.

If things change and you'd like to revisit [offer/goal], I'm here.

One last thing: is there anything I could have done differently? Your feedback genuinely helps me improve.

Wishing you the best either way."`}

**Goal:** Create a graceful exit that often triggers a reply.

---

## Soft Close Message

**Use when:** Lead has re-engaged but hasn't committed

**Message:**
"[Name], glad we reconnected.

Based on what you've shared, I think [offer] is a strong fit for where you're at.

Here's what I'd suggest as a next step: [specific low-commitment action — e.g., 15-min call, free audit, demo].

Want me to send you the link?"

**Goal:** Move from conversation to commitment with a low-friction ask.

---

## High-Intent Reply Handling

**When a lead replies to any message in this sequence:**

1. **Respond within 5 minutes** — speed is the #1 conversion factor
2. **Acknowledge their reply** — "Great to hear from you!"
3. **Ask one qualifying question** — don't pitch immediately
4. **Move to booking** — get them on a call or to the next step
5. **Do not send more automated messages** — switch to live conversation

**Script:**
"[Name]! Great to hear from you. Quick question before I share more — [qualifying question]?

Once I know that, I can point you to exactly the right next step."

---

*Generated by RecallCore Lead Automation · Deploy in order, stop when lead replies*
`;
}

function generateDeliveryHandoffChecklist(missionTitle, allText, actionCards, completedSteps) {
  const deliveryText = findCardByKeywords(actionCards, completedSteps, [
    'delivery', 'handoff', 'hand-off', 'onboarding', 'fulfillment', 'client',
    'post-sale', 'post sale', 'deliver', 'completed order',
  ]);
  const paymentText = findCardByKeywords(actionCards, completedSteps, [
    'payment', 'invoice', 'deposit', 'confirmation', 'payment confirmation',
  ]);
  const intelligence = extractMissionIntelligence(allText, 400);

  return `# Delivery Handoff Checklist
## Generated by RecallCore — ${missionTitle || 'Delivery Handoff Mission'}

Complete this checklist before and after every delivery or client handoff.
All items must be checked before marking a deal as "Delivered."

---

${intelligence ? `## Mission-Specific Intelligence\n\n${intelligence}\n\n---\n` : ''}

${deliveryText ? `## Mission-Specific Delivery Output\n\n${deliveryText?.slice(0, 600)}\n\n---\n` : ''}

## 1. Customer Information

- [ ] Full legal name confirmed
- [ ] Contact number verified (call or SMS test)
- [ ] Email address confirmed
- [ ] Delivery address or meeting location confirmed
- [ ] Preferred contact method noted
- [ ] Emergency contact collected (if applicable)

**Notes:** _______________________________________________

---

## 2. Payment Confirmation

- [ ] Full payment received OR deposit + payment plan confirmed
- [ ] Payment receipt sent to customer
- [ ] Invoice marked as paid in records
- [ ] Refund policy communicated and acknowledged
- [ ] Payment method on file for future charges (if applicable)

${paymentText ? `\n**From Mission Output:**\n${paymentText?.slice(0, 300)}\n` : ''}

**Notes:** _______________________________________________

---

## 3. Required Documents

- [ ] Purchase agreement or contract signed
- [ ] ID or verification collected (if required)
- [ ] Warranty or guarantee document provided
- [ ] Terms and conditions acknowledged
- [ ] Any compliance documents completed

**Notes:** _______________________________________________

---

## 4. Product / Unit Preparation

- [ ] Product/service confirmed as ready
- [ ] Quality check completed
- [ ] All components or deliverables accounted for
- [ ] Packaging or presentation prepared
- [ ] Any customizations or special requests fulfilled

**Notes:** _______________________________________________

---

## 5. Delivery Status

- [ ] Delivery date and time confirmed with customer
- [ ] Delivery method confirmed (in-person, shipping, digital)
- [ ] Tracking or confirmation number provided (if applicable)
- [ ] Customer notified of delivery window
- [ ] Delivery completed and confirmed by customer

**Notes:** _______________________________________________

---

## 6. Post-Delivery Follow-Up

- [ ] Thank you message sent within 24 hours
- [ ] Satisfaction check-in scheduled (Day 3 or Day 7)
- [ ] Referral request sent after positive feedback
- [ ] Review or testimonial requested
- [ ] Upsell or next offer introduced (if appropriate)

**Schedule:**
- Day 1: Thank you message
- Day 3: Check-in — "How is everything going?"
- Day 7: Satisfaction survey or review request
- Day 30: Referral ask or next offer

**Notes:** _______________________________________________

---

## 7. Issue Escalation

**If customer reports a problem:**

1. Acknowledge within 1 hour — "I hear you and I'm on it"
2. Identify the issue — ask clarifying questions
3. Offer a resolution within 24 hours
4. Document the issue and resolution in the client record
5. Follow up after resolution to confirm satisfaction

**Escalation Contacts:**
- Primary: [Your Name / Team Lead]
- Backup: [Secondary Contact]
- Refund Authority: [Name / Role]

**Notes:** _______________________________________________

---

## Handoff Sign-Off

| Item | Completed | Date | Initials |
|---|---|---|---|
| Customer info confirmed | | | |
| Payment confirmed | | | |
| Documents signed | | | |
| Product prepared | | | |
| Delivery completed | | | |
| Follow-up scheduled | | | |

**Delivered by:** _______________________
**Date:** _______________________
**Customer signature (if applicable):** _______________________

---

*Generated by RecallCore Delivery Crew · Complete before marking deal as closed*
`;
}

// ─── Main Export ──────────────────────────────────────────────────────────────

/**
 * generateStructuredAssets
 * @param {string} missionTitle - The mission title or intent text
 * @param {string} missionText - The original mission input text
 * @param {Array} actionCards - Array of action card objects from mission execution
 * @param {Object} completedSteps - Object of completed step outputs
 * @returns {Array} Array of asset objects with filename, format, mimeType, description, content
 */
export function generateStructuredAssets(missionTitle = '', missionText = '', actionCards = [], completedSteps = {}) {
  const category = detectMissionCategory(missionTitle, missionText);

  if (category !== 'sales_crew') {
    return [];
  }

  const allText = extractAllText(actionCards, completedSteps);

  // Task 7: Conditionally generate delivery_handoff_checklist.md
  const includeDelivery = hasDeliveryContext(missionTitle, missionText, allText);

  const assets = [
    {
      filename: 'sales_playbook.md',
      format: 'md',
      mimeType: 'text/markdown',
      description: 'Sales system playbook with ICP, offer positioning, qualification rules, and objection handling',
      content: generateSalesPlaybook(missionTitle, allText, actionCards, completedSteps),
    },
    {
      filename: 'follow_up_sequence.csv',
      format: 'csv',
      mimeType: 'text/csv',
      description: 'Step-by-step Follow-Up Engine with timing, channel, message copy, and trigger conditions',
      content: generateFollowUpSequenceCSV(missionTitle, allText, actionCards, completedSteps),
    },
    {
      filename: 'objection_scripts.md',
      format: 'md',
      mimeType: 'text/markdown',
      description: 'Ready-to-use scripts for price, trust, timing, and no-response objections',
      content: generateObjectionScripts(missionTitle, allText, actionCards, completedSteps),
    },
    {
      filename: 'pipeline_config.json',
      format: 'json',
      mimeType: 'application/json',
      description: 'Pipeline Movement config: stages, lead status rules, hot/warm/cold thresholds, and conversion events',
      content: generatePipelineConfig(missionTitle, allText, actionCards, completedSteps),
    },
    {
      filename: 'buyer_qualification_checklist.md',
      format: 'md',
      mimeType: 'text/markdown',
      description: 'Qualification checklist covering budget, use case, timeline, decision-maker, and payment readiness',
      content: generateBuyerQualificationChecklist(missionTitle, allText, actionCards, completedSteps),
    },
    {
      filename: 'lead_reactivation_sequence.md',
      format: 'md',
      mimeType: 'text/markdown',
      description: 'Revenue Recovery sequence for cold leads, stalled conversations, and silent prospects',
      content: generateLeadReactivationSequence(missionTitle, allText, actionCards, completedSteps),
    },
  ];

  // Only include delivery handoff checklist when delivery/handoff context is present
  if (includeDelivery) {
    assets?.push({
      filename: 'delivery_handoff_checklist.md',
      format: 'md',
      mimeType: 'text/markdown',
      description: 'Delivery Handoff checklist including payment confirmation, documents, and post-delivery follow-up',
      content: generateDeliveryHandoffChecklist(missionTitle, allText, actionCards, completedSteps),
    });
  }

  return assets;
}

export default generateStructuredAssets;
